import { createContext, useContext, useEffect } from 'react';
import { useGetTrueVaultConsent } from './FueledTrueVault';
import { TRACKING_EVENTS } from './fueled.events';

const defaultContext = {
  clients: {},
  register: () => () => null,
  publish: () => null,
  subscribe: () => () => null,
  consent: undefined,
};

const FueledAnalyticsContext = createContext(defaultContext);

const eventCache = new Map();
const subscribers = new Map();
const clients = {};

const areClientsReady = () => {
  return (
    Object.values(clients).every(Boolean) && Object.keys(clients).length > 0
  );
};

const subscribe = (eventName, callback) => {
  if (!subscribers.has(eventName)) {
    subscribers.set(eventName, new Set());
  }
  subscribers.get(eventName).add(callback);

  return () => {
    const callbacks = subscribers.get(eventName);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        subscribers.delete(eventName);
      }
    }
  };
};

const publishEvent = (eventName, payload) => {
  (subscribers.get(eventName) ?? new Map()).forEach((callback) => {
    try {
      callback(payload);
    } catch (error) {
      if (typeof error === 'object' && error instanceof Error) {
        console.error(error.message);
      } else {
        console.error(error);
      }
    }
  });
};

const publish = (eventName, payload) => {
  if (!areClientsReady()) {
    return eventCache.set(eventName, payload);
  }

  return publishEvent(eventName, payload);
};

const register = (client) => {
  if (typeof clients[client] === 'undefined') {
    clients[client] = false;
  }

  return () => {
    clients[client] = true;

    if (areClientsReady() && eventCache.size > 0) {
      eventCache.forEach((payload, eventName) => {
        publishEvent(eventName, payload);
      });

      eventCache.clear();
    }
  };
};

const useListenToTrackingEvents = () => {
  useEffect(() => {
    const eventNames = Object.values(TRACKING_EVENTS);

    const handleEvent = (event) => {
      publish(event.type, event);
    };

    eventNames.forEach((eventName) => {
      window.addEventListener(eventName, handleEvent);
    });

    return () => {
      eventNames.forEach((eventName) => {
        window.removeEventListener(eventName, handleEvent);
      });
    };
  }, []);
};

export function FueledAnalyticsProvider({ children }) {
  useListenToTrackingEvents();

  const consent = useGetTrueVaultConsent();

  return (
    <FueledAnalyticsContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        clients,
        register,
        subscribe,
        publish,
        consent,
      }}
    >
      {children}
    </FueledAnalyticsContext.Provider>
  );
}

export const useAnalytics = () => {
  const context = useContext(FueledAnalyticsContext);
  if (!context) {
    throw new Error(
      'useAnalytics must be used within a FueledAnalyticsProvider'
    );
  }

  return context;
};
